import React from "react";
import Footer from "../components/Footer/Footer";
import "./Page.css";

function Privacy() {
  return (
    <div className="page-container">
      <div className="content-card">
        <h1 className="page-title">Privacy Policy</h1>
        
        <div className="page-content">
          <p className="last-updated">Last Updated: December 2025</p>

          <p>
            The Sixteenth Degree (Private) Limited ("we", "us", or "our") is committed to protecting your privacy with the same precision and integrity we apply to our products. This Privacy Policy explains how we collect, use, and safeguard information across our website and any mobile applications we develop.
          </p>
          <p>
            We believe that privacy is a fundamental right, and we approach data protection with methodical care and respect for your personal information. This document outlines our practices and your rights regarding the information we handle.
          </p>

          <div className="info-section">
            <h2>Information We Collect</h2>
            <p>We collect only the information necessary to provide and improve our services. This may include:</p>
            <ul>
              <li>
                <strong>Personal Information:</strong> Basic contact information that you voluntarily provide (such as your name, email address, or phone number) when you contact us or use our services.
              </li>
              <li>
                <strong>Technical Information:</strong> Device type, operating system, browser type, IP address, pages viewed, and usage patterns for analytics and website performance optimization.
              </li>
              <li>
                <strong>Application Data:</strong> Information generated through your use of our mobile applications, including preferences, settings, and usage statistics, stored locally on your device or synchronized with our servers when you choose to do so.
              </li>
              <li>
                <strong>Communication Records:</strong> Correspondence with our support team, including emails and support tickets, which we retain to provide better service and resolve issues.
              </li>
            </ul>
          </div>

          <div className="info-section">
            <h2>How We Use Information</h2>
            <p>We use collected information exclusively for legitimate business purposes, including:</p>
            <ul>
              <li>Improving our website and digital products through analytics and user feedback</li>
              <li>Providing responsive customer support and technical assistance</li>
              <li>Communicating important updates, security notices, or service changes</li>
              <li>Maintaining the security, stability, and performance of our services</li>
              <li>Personalizing your experience and tailoring content to your preferences</li>
              <li>Conducting research and development to enhance our offerings</li>
              <li>Complying with legal obligations and protecting our rights</li>
            </ul>
            <p>
              We do not use your information for purposes beyond what is described in this policy without your explicit consent.
            </p>
          </div>

          <div className="info-section">
            <h2>Sharing of Information</h2>
            <p>
              We do not sell or share your personal information with third parties except:
            </p>
            <ul>
              <li>When required by law</li>
              <li>
                With trusted service providers who assist us in operating our services (and who must keep your data confidential)
              </li>
            </ul>
          </div>

          <div className="info-section">
            <h2>Data Security</h2>
            <p>
              We take reasonable technical and administrative measures to protect your information from unauthorized access, alteration, or disclosure. Our security practices include:
            </p>
            <ul>
              <li>Encryption of data in transit using industry-standard protocols (TLS/SSL)</li>
              <li>Secure storage of sensitive information with access controls and authentication</li>
              <li>Regular security assessments and updates to our systems</li>
              <li>Limited access to personal data on a need-to-know basis for our team members</li>
              <li>Monitoring and logging of access to detect and prevent unauthorized activities</li>
            </ul>
            <p>
              While we strive to protect your information, no method of transmission over the internet or electronic storage is completely secure. We cannot guarantee absolute security but are committed to maintaining the highest standards of data protection.
            </p>
          </div>

          <div className="info-section">
            <h2>Your Rights</h2>
            <p>You have certain rights regarding your personal information. You may contact us at any time to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request corrections to inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information where applicable and legally permissible</li>
              <li><strong>Objection:</strong> Object to certain processing activities, such as direct marketing</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service provider in a structured format</li>
              <li><strong>Restriction:</strong> Request limitation of how we process your information in certain circumstances</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided in the "Contact Us" section below. We will respond to your request within a reasonable timeframe and in accordance with applicable laws.
            </p>
          </div>

          <div className="info-section">
            <h2>Cookies and Tracking Technologies</h2>
            <p>
              Our website may use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and improve our services. Cookies are small text files stored on your device that help us remember your preferences and understand how you interact with our website.
            </p>
            <p>
              You can control cookie preferences through your browser settings. However, disabling certain cookies may limit your ability to use some features of our website.
            </p>
          </div>

          <div className="info-section">
            <h2>Third-Party Services</h2>
            <p>
              Our services may integrate with third-party services for analytics, hosting, payment processing, or other functions. These services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of third-party services.
            </p>
          </div>

          <div className="info-section">
            <h2>Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law. When information is no longer needed, we will securely delete or anonymize it in accordance with our data retention policies.
            </p>
          </div>

          <div className="info-section">
            <h2>Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 13. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete such information promptly.
            </p>
          </div>

          <div className="info-section">
            <h2>International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We take appropriate measures to ensure that your information receives adequate protection in accordance with this Privacy Policy.
            </p>
          </div>

          <div className="info-section">
            <h2>Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on this page and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </div>

          <div className="info-section">
            <h2>Contact Us</h2>
            <p>
              For privacy-related questions or concerns, please contact:{" "}
              <a href="mailto:privacy@thesixteenthdegree.com">
                privacy@thesixteenthdegree.com
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Privacy;

